import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, LineChart, Line, Legend
} from 'recharts';
import { api } from '../api';

function getDefaultDates() {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 29);
  return {
    start: start.toISOString().split('T')[0],
    end: end.toISOString().split('T')[0],
  };
}

export default function MileageChart({ subAccountId, trackers, selectedTracker, onSelectTracker }) {
  const defaults = getDefaultDates();
  const [startDate, setStartDate] = useState(defaults.start);
  const [endDate, setEndDate] = useState(defaults.end);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [chartType, setChartType] = useState('bar'); // 'bar' | 'cumulative'

  const tracker = selectedTracker || trackers[0];

  useEffect(() => {
    if (!tracker) return;
    setLoading(true);
    api.getHistory(subAccountId, tracker.id, startDate, endDate)
      .then(data => {
        // Build cumulative series
        let cumulative = 0;
        const enriched = data.map(d => {
          cumulative += d.miles_traveled;
          return { ...d, cumulative: Math.round(cumulative * 10) / 10, miles: Math.round(d.miles_traveled * 10) / 10 };
        });
        setHistory(enriched);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [tracker, subAccountId, startDate, endDate]);

  const totalMiles = history.reduce((sum, d) => sum + d.miles_traveled, 0);
  const avgDaily = history.length ? totalMiles / history.length : 0;
  const maxDay = history.reduce((max, d) => d.miles_traveled > (max?.miles_traveled || 0) ? d : max, null);

  return (
    <div className="mileage-panel">
      {/* Controls */}
      <div className="mileage-controls">
        <div className="tracker-select-group">
          <label>Trailer</label>
          <select
            value={tracker?.id || ''}
            onChange={e => onSelectTracker(trackers.find(t => t.id === e.target.value))}
          >
            {trackers.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
        <div className="date-range-group">
          <label>From</label>
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} max={endDate}/>
          <label>To</label>
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} min={startDate}/>
        </div>
        <div className="chart-type-toggle">
          <button className={chartType === 'bar' ? 'active' : ''} onClick={() => setChartType('bar')}>Daily</button>
          <button className={chartType === 'cumulative' ? 'active' : ''} onClick={() => setChartType('cumulative')}>Cumulative</button>
        </div>
      </div>

      {/* Summary stats */}
      <div className="mileage-stats">
        <div className="mileage-stat-card">
          <span className="mileage-stat-value">{Math.round(totalMiles).toLocaleString()}</span>
          <span className="mileage-stat-label">Total miles</span>
        </div>
        <div className="mileage-stat-card">
          <span className="mileage-stat-value">{Math.round(avgDaily)}</span>
          <span className="mileage-stat-label">Avg miles/day</span>
        </div>
        <div className="mileage-stat-card">
          <span className="mileage-stat-value">{maxDay ? Math.round(maxDay.miles_traveled) : '—'}</span>
          <span className="mileage-stat-label">Peak day</span>
        </div>
        <div className="mileage-stat-card">
          <span className="mileage-stat-value">{tracker ? Math.round(tracker.total_miles).toLocaleString() : '—'}</span>
          <span className="mileage-stat-label">Odometer</span>
        </div>
      </div>

      {/* Chart */}
      {loading ? (
        <div className="chart-loading"><div className="spinner"/></div>
      ) : (
        <div className="chart-area">
          <ResponsiveContainer width="100%" height={320}>
            {chartType === 'bar' ? (
              <BarChart data={history} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false}/>
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={d => d.slice(5)}/>
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${v}mi`}/>
                <Tooltip
                  formatter={v => [`${Math.round(v)} mi`, 'Miles traveled']}
                  labelFormatter={l => `Date: ${l}`}
                />
                <Bar dataKey="miles" fill="#f97316" radius={[3,3,0,0]}/>
              </BarChart>
            ) : (
              <LineChart data={history} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false}/>
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={d => d.slice(5)}/>
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${v}mi`}/>
                <Tooltip formatter={v => [`${Math.round(v)} mi`, 'Cumulative miles']}/>
                <Line type="monotone" dataKey="cumulative" stroke="#f97316" strokeWidth={2} dot={false}/>
              </LineChart>
            )}
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
